import {
    AlertTriangle,
    ArrowRight,
    ChevronLeft,
    LoaderCircle,
    Plus,
    Minus,
    NotebookText,
    CalendarDays,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ManualLeaveAdjustmentScreen.module.css'

const LEAVE_TYPES = ['CL', 'SL', 'PL', 'CO']

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const resolveAvailableBalance = (currentBalances, leaveType) => {
    if (!currentBalances || !leaveType) return null

    if (Array.isArray(currentBalances)) {
        const found = currentBalances.find((item) => String(item?.leaveType || '').toUpperCase() === leaveType)
        if (!found) return null
        return toNumber(found?.availableBalance ?? found?.available ?? found?.remaining ?? 0)
    }

    if (typeof currentBalances === 'object') {
        const row = currentBalances[leaveType]
        if (!row) return null
        if (typeof row === 'number') return toNumber(row)
        return toNumber(row?.availableBalance ?? row?.available ?? row?.remaining ?? 0)
    }

    return null
}

const ManualLeaveAdjustmentScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { staffIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const staffName = location?.state?.staffName || 'Staff'
    const preselectedLeaveType = String(location?.state?.preselectedLeaveType || 'CL').toUpperCase()
    const currentBalances = location?.state?.currentBalances || null

    const [formData, setFormData] = useState({
        leaveType: LEAVE_TYPES.includes(preselectedLeaveType) ? preselectedLeaveType : 'CL',
        days: '',
        direction: 'CREDIT',
        transactionDate: new Date().toISOString().split('T')[0],
        remarks: '',
    })

    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const selectedTypeBalance = useMemo(() => {
        return resolveAvailableBalance(currentBalances, formData.leaveType)
    }, [currentBalances, formData.leaveType])

    const projectedBalance = useMemo(() => {
        const current = selectedTypeBalance ?? 0
        const days = parseFloat(formData.days)
        if (!Number.isFinite(days) || days <= 0) return null

        const next = formData.direction === 'CREDIT' ? current + days : current - days
        if (next < 0) return null
        return Math.round(next * 10) / 10
    }, [formData.days, formData.direction, selectedTypeBalance])

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        setErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const submitManualAdjustment = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Missing authentication or store information.')
            return
        }
        if (!staffIdentifier) {
            toast.error('Staff identifier is missing.')
            return
        }

        const newErrors = {}
        const parsedDays = parseFloat(formData.days)

        if (!formData.leaveType) newErrors.leaveType = 'Select a leave type.'
        if (!formData.days || !Number.isFinite(parsedDays) || parsedDays <= 0) {
            newErrors.days = 'Enter a valid number of days.'
        }
        if (Number.isFinite(parsedDays) && parsedDays > 365) {
            newErrors.days = 'Cannot exceed 365 days.'
        }
        if (!formData.transactionDate) newErrors.transactionDate = 'Select a date.'
        if (!String(formData.remarks || '').trim()) newErrors.remarks = 'Remarks are required.'

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'creditManualLeave', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier,
                    createdBy: authCtx?.authIdentifier || '',
                    leaveType: formData.leaveType,
                    days: parsedDays,
                    direction: formData.direction,
                    transactionDate: formData.transactionDate,
                    remarks: formData.remarks.trim(),
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const payload = respo?.response || {}
                ;(payload?.warnings || []).forEach((warning) => {
                    if (warning?.message) toast.warning(warning.message)
                })

                toast.success(
                    `${payload?.days ?? parsedDays} ${payload?.leaveType || formData.leaveType} day(s) ${
                        payload?.direction === 'DEBIT' ? 'debited' : 'credited'
                    }. New balance: ${toNumber(payload?.newBalance, 0).toFixed(1)} days.`
                )
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (String(respo?.message || '').toLowerCase().includes('remarks')) {
                        setErrors((prev) => ({ ...prev, remarks: respo?.message || 'Remarks are required.' }))
                    } else {
                        toast.error(respo?.message || 'Missing required fields.')
                    }
                    break
                case 'VAL_002':
                    setErrors((prev) => ({ ...prev, days: respo?.message || 'Invalid days value.' }))
                    break
                case 'VAL_003':
                    setErrors((prev) => ({ ...prev, leaveType: respo?.message || 'Invalid leave type.' }))
                    break
                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, transactionDate: 'Invalid date.' }))
                    break
                case 'VAL_007':
                    toast.error('Staff not found.')
                    navigate(-1)
                    break
                case 'LEAVE_001':
                    toast.error(respo?.message || 'No leave balance found. Initialize first.')
                    navigate('/payroll/setup/leave-balance-init', {
                        state: {
                            preselectedStaff: [{ staffIdentifier, staffName }],
                        },
                    })
                    break
                case 'LEAVE_005':
                    setErrors((prev) => ({
                        ...prev,
                        days: `Only ${toNumber(respo?.response?.currentAvailableBalance, 0).toFixed(1)} days available to debit.`,
                    }))
                    break
                case 'SYS_002':
                    toast.error(respo?.message || 'Adjustment failed.')
                    break
                default:
                    toast.error(respo?.message || 'Adjustment failed.')
                    break
            }
        } catch (error) {
            console.error('creditManualLeave error:', error)
            toast.error('Network error.')
        } finally {
            setIsSaving(false)
        }
    }

    if (!staffIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/payroll/leave-balance-overview')}>
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <h1 className={styles.headerTitle}>Manual Leave Adjustment</h1>
                        <p className={styles.headerSub}>No staff selected. Open this screen from a staff leave card.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Manual Leave Adjustment</h1>
                    <p className={styles.headerSub}>for {staffName}</p>
                </div>
            </div>

            <div className={styles.sectionCard}>
                <label className={styles.fieldLabel}>Leave Type *</label>
                <div className={styles.segmentBtnGroup}>
                    {LEAVE_TYPES.map((type) => (
                        <button
                            key={type}
                            type="button"
                            className={`${styles.segmentBtn} ${formData.leaveType === type ? styles.segmentBtnActive : ''}`}
                            onClick={() => setField('leaveType', type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
                <p className={styles.fieldHint}>
                    Current balance: {selectedTypeBalance === null ? '—' : `${selectedTypeBalance.toFixed(1)} days`}
                </p>
                {errors.leaveType && <p className={styles.fieldError}>{errors.leaveType}</p>}
            </div>

            <div className={styles.sectionCard}>
                <label className={styles.fieldLabel}>Direction</label>
                <div className={styles.segmentBtnGroup}>
                    <button
                        type="button"
                        className={`${styles.segmentBtn} ${
                            formData.direction === 'CREDIT' ? styles.segmentBtnActive : ''
                        }`}
                        onClick={() => setField('direction', 'CREDIT')}
                    >
                        <Plus size={14} /> Credit (Add)
                    </button>
                    <button
                        type="button"
                        className={`${styles.segmentBtn} ${
                            formData.direction === 'DEBIT' ? styles.segmentBtnActive : ''
                        }`}
                        onClick={() => setField('direction', 'DEBIT')}
                    >
                        <Minus size={14} /> Debit (Remove)
                    </button>
                </div>
                {formData.direction === 'DEBIT' && (
                    <div className={styles.warningRow}>
                        <AlertTriangle size={14} /> This will reduce the staff's leave balance.
                    </div>
                )}
            </div>

            <div className={styles.sectionCard}>
                <label className={styles.fieldLabel}>Number of Days *</label>
                <input
                    type="number"
                    step="0.5"
                    min="0"
                    className={`${styles.textInput} ${errors.days ? styles.inputError : ''}`}
                    value={formData.days}
                    onChange={(event) => setField('days', event.target.value)}
                    placeholder="e.g. 1.0"
                />
                <p className={styles.fieldHint}>e.g. 1.0 for one full day, 0.5 for half day</p>
                {formData.direction === 'DEBIT' && selectedTypeBalance !== null && (
                    <p className={styles.fieldHint}>Max: {selectedTypeBalance.toFixed(1)} days</p>
                )}
                {errors.days && <p className={styles.fieldError}>{errors.days}</p>}

                {projectedBalance !== null && (
                    <div
                        className={`${styles.previewCard} ${
                            formData.direction === 'CREDIT' ? styles.previewCredit : styles.previewDebit
                        }`}
                    >
                        After this adjustment: <strong>{projectedBalance.toFixed(1)} days</strong>
                    </div>
                )}
            </div>

            <div className={styles.sectionCard}>
                <label className={styles.fieldLabel}>Effective Date *</label>
                <div className={styles.dateWrap}>
                    <CalendarDays size={15} />
                    <input
                        type="date"
                        className={`${styles.textInput} ${errors.transactionDate ? styles.inputError : ''}`}
                        value={formData.transactionDate}
                        onChange={(event) => setField('transactionDate', event.target.value)}
                    />
                </div>
                {errors.transactionDate && <p className={styles.fieldError}>{errors.transactionDate}</p>}
            </div>

            <div className={styles.sectionCard}>
                <label className={styles.fieldLabel}>Reason for Adjustment *</label>
                <div className={styles.remarksWrap}>
                    <NotebookText size={15} />
                    <textarea
                        className={`${styles.textArea} ${errors.remarks ? styles.inputError : ''}`}
                        rows={3}
                        value={formData.remarks}
                        onChange={(event) => setField('remarks', event.target.value)}
                        placeholder="e.g. Compensatory off for working on Holi April 5"
                    />
                </div>
                <p className={styles.charCount}>{formData.remarks.length} character(s)</p>
                {errors.remarks && <p className={styles.fieldError}>{errors.remarks}</p>}
            </div>

            <button type="button" onClick={submitManualAdjustment} disabled={isSaving} className={styles.saveBtn}>
                {isSaving ? (
                    <>
                        <LoaderCircle size={16} className={styles.spinIcon} /> Saving...
                    </>
                ) : (
                    <>
                        {formData.direction === 'CREDIT' ? 'Credit Leave' : 'Debit Leave'} <ArrowRight size={15} />
                    </>
                )}
            </button>
        </div>
    )
}

export default ManualLeaveAdjustmentScreen